/** AML verification: the operations register kept as JSON on the record (see aml-register.ts). */
import React, { useState } from "react";
import { useUpdate } from "@refinedev/core";
import { Button, Card, Col, DatePicker, Form, Input, InputNumber, Modal, Popconfirm, Row, Select, Space, Statistic, Table, Typography } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, TransactionOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { StatusTag } from "../fields";
import { parseRegister, registerStats, serializeRegister, TX_TYPES, type Transaction, type TxClass } from "./aml-register";

const CLASSES: Array<{ value: TxClass; label: string }> = [
  { value: "da_verificare", label: "Da verificare" },
  { value: "normale", label: "Normale" },
  { value: "sospetta", label: "Sospetta" },
];
const eur = (v: number) => v.toLocaleString("it-IT", { style: "currency", currency: "EUR" });

export const AmlRegister: React.FC<{ record: Record<string, unknown> }> = ({ record }) => {
  const [description, setDescription] = useState(record.description);
  const reg = parseRegister(description);
  const stats = registerStats(reg);
  const { mutate, isLoading } = useUpdate();
  const [editing, setEditing] = useState<Transaction | "new" | null>(null);
  const [form] = Form.useForm();

  const save = (transactions: Transaction[]) => {
    const next = serializeRegister(description, { ...reg, transactions });
    mutate({ resource: "cmp_aml_verification", id: String(record.id), values: { description: next } }, { onSuccess: () => setDescription(next) });
  };
  const open = (tx: Transaction | "new") => {
    setEditing(tx);
    form.setFieldsValue(tx === "new" ? { date: dayjs(), type: "bonifico" } : { ...tx, date: dayjs(tx.date) });
  };
  const submit = async () => {
    const v = await form.validateFields();
    const fields = { date: v.date.format("YYYY-MM-DD"), description: v.description, amount: v.amount, counterpart: v.counterpart ?? "", type: v.type, notes: v.notes ?? "" };
    save(editing === "new"
      ? [...reg.transactions, { id: `tx_${Date.now()}`, classification: "da_verificare", ...fields }]
      : reg.transactions.map((t) => (editing && t.id === editing.id ? { ...t, ...fields } : t)));
    setEditing(null);
  };

  return <Card size="small" style={{ marginTop: 16 }} title={<Space><TransactionOutlined />Operazioni registrate</Space>}
    extra={<Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => open("new")}>Registra operazione</Button>}>
    <Row gutter={16} style={{ marginBottom: 12 }}>
      <Col span={6}><Statistic title="Operazioni" value={stats.total} /></Col>
      <Col span={6}><Statistic title="Volume totale" value={eur(stats.volume)} /></Col>
      <Col span={6}><Statistic title="Da verificare" value={stats.toVerify} valueStyle={{ color: stats.toVerify ? "#d48806" : undefined }} /></Col>
      <Col span={6}><Statistic title="Sospette" value={stats.suspicious} valueStyle={{ color: stats.suspicious ? "#cf1322" : undefined }} /></Col>
    </Row>
    {stats.cashOverThreshold > 0 && <Typography.Paragraph type="warning">{stats.cashOverThreshold} operazioni in contanti pari o superiori a 5.000 €.</Typography.Paragraph>}
    <Table<Transaction> size="small" rowKey="id" loading={isLoading} pagination={{ pageSize: 10 }}
      dataSource={[...reg.transactions].sort((a, b) => b.date.localeCompare(a.date))}
      columns={[
        { title: "Data", dataIndex: "date", width: 110, render: (d: string) => dayjs(d).format("DD/MM/YYYY") },
        { title: "Descrizione", dataIndex: "description" },
        { title: "Controparte", dataIndex: "counterpart" },
        { title: "Tipo", dataIndex: "type", width: 110 },
        { title: "Importo", dataIndex: "amount", align: "right", width: 130, render: (v: number) => eur(Number(v) || 0) },
        { title: "Classificazione", dataIndex: "classification", width: 170, filters: CLASSES.map((c) => ({ text: c.label, value: c.value })), onFilter: (v, r) => r.classification === v,
          render: (c: TxClass, tx) => <Select size="small" style={{ width: 150 }} value={c}
            options={CLASSES.map((o) => ({ value: o.value, label: <StatusTag value={o.label} /> }))}
            onChange={(next: TxClass) => save(reg.transactions.map((t) => (t.id === tx.id ? { ...t, classification: next } : t)))} /> },
        { width: 80, render: (_, tx) => <Space>
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => open(tx)} />
          <Popconfirm title="Eliminare questa operazione?" onConfirm={() => save(reg.transactions.filter((t) => t.id !== tx.id))}>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space> },
      ]} />
    <Modal open={editing !== null} title={editing === "new" ? "Registra nuova operazione" : "Modifica operazione"} onOk={submit} onCancel={() => setEditing(null)} okText="Salva">
      <Form form={form} layout="vertical">
        <Form.Item name="date" label="Data" rules={[{ required: true }]}><DatePicker format="DD/MM/YYYY" /></Form.Item>
        <Form.Item name="description" label="Descrizione operazione" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="counterpart" label="Controparte"><Input /></Form.Item>
        <Form.Item name="type" label="Tipo" rules={[{ required: true }]}><Select options={TX_TYPES.map((t) => ({ value: t, label: t }))} /></Form.Item>
        <Form.Item name="amount" label="Importo (EUR)" rules={[{ required: true }]}><InputNumber min={0} step={100} style={{ width: "100%" }} /></Form.Item>
        <Form.Item name="notes" label="Note"><Input.TextArea rows={2} /></Form.Item>
      </Form>
    </Modal>
  </Card>;
};
